import React from 'react';
import { Link } from 'react-router-dom';

const Footer: React.FC = () => {
  return (
    <footer className="relative bg-forest-900 text-cream-100 overflow-hidden bg-linen">

      {/* Giant watermark */}
      <div className="absolute inset-0 pointer-events-none select-none overflow-hidden">
        <span className="absolute -bottom-[0.15em] left-1/2 -translate-x-1/2 font-display text-[18vw] lg:text-[14vw] font-bold tracking-[-0.05em] text-cream-100/[0.02] leading-none whitespace-nowrap">
          LiberaCure
        </span>
      </div>

      {/* Top botanical divider */}
      <div className="relative section-container pt-1">
        <div className="flex items-center gap-4">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gold-400/30 to-transparent" />
          <svg className="w-5 h-5 text-gold-400/40" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14.5v-5l-4.5 4.5c1.05 1.38 2.7 2.28 4.5 2.5zm1-1v-5l4.5-4.5c-1.05-1.38-2.7-2.28-4.5-2.5v12z" opacity="0.6"/>
          </svg>
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gold-400/30 to-transparent" />
        </div>
      </div>

      {/* Marquee trust strip */}
      <div className="border-b border-cream-100/[0.06] overflow-hidden">
        <div className="flex animate-marquee">
          {[...Array(2)].map((_, setIdx) => (
            <div key={setIdx} className="flex items-center gap-8 sm:gap-12 py-4 shrink-0 px-4 sm:px-6">
              {[
                'Worldwide Shipping', 'Licensed Pharmacy', '100% Authentic',
                'Discreet Packaging', '24/7 Support', 'Secure Payment',
                'Privacy First', 'Money-Back Guarantee'
              ].map((text, i) => (
                <React.Fragment key={`${setIdx}-${i}`}>
                  <span className="text-[11px] sm:text-[12px] font-medium text-cream-500 uppercase tracking-[0.15em] whitespace-nowrap">
                    {text}
                  </span>
                  <span className="w-1 h-1 rounded-full bg-gold-400/30 shrink-0" />
                </React.Fragment>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Main content — asymmetric split */}
      <div className="relative section-container pt-16 lg:pt-20 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8">

          {/* Left — Big CTA block (5 cols) */}
          <div className="lg:col-span-5">
            <Link to="/" className="inline-flex items-baseline gap-0 mb-6">
              <span className="font-display text-[28px] sm:text-[32px] font-bold tracking-[-0.04em] text-cream-100">Libera</span>
              <span className="font-display text-[28px] sm:text-[32px] font-bold tracking-[-0.04em] text-gold-400">Cure</span>
            </Link>
            <p className="text-[15px] sm:text-[16px] text-cream-400 leading-relaxed max-w-sm mb-8">
              Affordable medications delivered worldwide with complete privacy and discretion.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/shop"
                className="inline-flex items-center justify-center gap-2.5 px-7 py-3.5 bg-gold-400 text-forest-900 text-[14px] font-semibold rounded-lg hover:bg-gold-500 transition-all duration-300 ease-spring shadow-gold-glow"
              >
                Shop Now
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </Link>
              <Link
                to="/inquiry"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 border border-cream-100/15 text-[14px] font-medium text-cream-300 rounded-lg hover:bg-cream-100/5 hover:border-cream-100/25 transition-all duration-300"
              >
                Contact Us
              </Link>
            </div>
          </div>

          {/* Right — Compact link columns (7 cols) */}
          <div className="lg:col-span-7">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-8">
              {/* Shop */}
              <div>
                <h4 className="text-[11px] font-semibold text-cream-500 uppercase tracking-[0.15em] mb-5">Shop</h4>
                <nav className="space-y-3.5">
                  <Link to="/shop/sexual-health" className="block text-[14px] text-cream-400 hover:text-gold-400 transition-colors duration-200">Sexual Health</Link>
                  <Link to="/shop/hair-loss" className="block text-[14px] text-cream-400 hover:text-gold-400 transition-colors duration-200">Hair Loss</Link>
                  <Link to="/shop/skin-care" className="block text-[14px] text-cream-400 hover:text-gold-400 transition-colors duration-200">Skin Care</Link>
                  <Link to="/shop/weight-management" className="block text-[14px] text-cream-400 hover:text-gold-400 transition-colors duration-200">Weight Management</Link>
                </nav>
              </div>

              {/* Information */}
              <div>
                <h4 className="text-[11px] font-semibold text-cream-500 uppercase tracking-[0.15em] mb-5">Information</h4>
                <nav className="space-y-3.5">
                  <Link to="/honestly" className="block text-[14px] text-cream-400 hover:text-gold-400 transition-colors duration-200">Read Us Honestly</Link>
                  <Link to="/policy/indian-pharmacies" className="block text-[14px] text-cream-400 hover:text-gold-400 transition-colors duration-200">Indian Pharmacies</Link>
                  <Link to="/policy/regulated-medicine" className="block text-[14px] text-cream-400 hover:text-gold-400 transition-colors duration-200">Regulated Medicine</Link>
                  <Link to="/policy/protect-yourself" className="block text-[14px] text-cream-400 hover:text-gold-400 transition-colors duration-200">Protect Yourself</Link>
                  <Link to="/policy/safe-and-secure-shopping" className="block text-[14px] text-cream-400 hover:text-gold-400 transition-colors duration-200">Safe Shopping</Link>
                  <Link to="/policy/faq" className="block text-[14px] text-cream-400 hover:text-gold-400 transition-colors duration-200">FAQ</Link>
                </nav>
              </div>

              {/* Policies */}
              <div>
                <h4 className="text-[11px] font-semibold text-cream-500 uppercase tracking-[0.15em] mb-5">Policies</h4>
                <nav className="space-y-3.5">
                  <Link to="/policy/medicine-prescription-policy" className="block text-[14px] text-cream-400 hover:text-gold-400 transition-colors duration-200">Prescription</Link>
                  <Link to="/policy/shipping-dispatch-policy" className="block text-[14px] text-cream-400 hover:text-gold-400 transition-colors duration-200">Shipping & Dispatch</Link>
                  <Link to="/policy/cancellation-and-refund-policy" className="block text-[14px] text-cream-400 hover:text-gold-400 transition-colors duration-200">Cancellation & Refund</Link>
                  <Link to="/policy/communication-policy" className="block text-[14px] text-cream-400 hover:text-gold-400 transition-colors duration-200">Communication</Link>
                  <Link to="/policy/disclaimer" className="block text-[14px] text-cream-400 hover:text-gold-400 transition-colors duration-200">Disclaimer</Link>
                  <Link to="/policy/import-legality" className="block text-[14px] text-cream-400 hover:text-gold-400 transition-colors duration-200">Import Rules</Link>
                  <Link to="/policy/restricted-substances" className="block text-[14px] text-cream-400 hover:text-gold-400 transition-colors duration-200">What We Don&rsquo;t Ship</Link>
                </nav>
              </div>

              {/* Support */}
              <div>
                <h4 className="text-[11px] font-semibold text-cream-500 uppercase tracking-[0.15em] mb-5">Support</h4>
                <nav className="space-y-3.5">
                  <Link to="/inquiry" className="block text-[14px] text-cream-400 hover:text-gold-400 transition-colors duration-200">Contact</Link>
                  <Link to="/tracking" className="block text-[14px] text-cream-400 hover:text-gold-400 transition-colors duration-200">Track Order</Link>
                  <Link to="/payment-guide" className="block text-[14px] text-cream-400 hover:text-gold-400 transition-colors duration-200">Payment Guide</Link>
                  <Link to="/mission" className="block text-[14px] text-cream-400 hover:text-gold-400 transition-colors duration-200">Our Mission</Link>
                  <Link to="/notices" className="block text-[14px] text-cream-400 hover:text-gold-400 transition-colors duration-200">Notices</Link>
                </nav>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-14 pt-6 border-t border-cream-100/[0.06] flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-[12px] text-cream-600">
            &copy; 2026 LiberaCure. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link to="/terms" className="text-[12px] text-cream-500 hover:text-gold-400 transition-colors">Terms of Service</Link>
            <span className="w-px h-3 bg-cream-100/10" />
            <Link to="/privacy" className="text-[12px] text-cream-500 hover:text-gold-400 transition-colors">Privacy Policy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
